import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { api, formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, Clock, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const fieldCls =
  "w-full bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-3 text-[#0A1128] placeholder:text-[#3A435E]/40 text-sm tracking-wide";

const MeetingPage = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "10:00",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const launchMailto = () => {
    const subject = encodeURIComponent(t.meeting.email_subject);
    const bodyLines = [
      `${t.meeting.name}: ${form.name}`,
      `${t.meeting.email}: ${form.email}`,
      `${t.meeting.phone}: ${form.phone || "—"}`,
      "",
      `${t.meeting.date}: ${form.date}`,
      `${t.meeting.time}: ${form.time}`,
      "",
      form.message || "",
      "",
      "— StayZen",
    ];
    const body = encodeURIComponent(bodyLines.join("\n"));
    window.location.href = `mailto:${t.meeting.email_to}?subject=${subject}&body=${body}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/meetings", form);
      toast.success(t.meeting.success);
      launchMailto();
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.meeting.error);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-[#F4F1EB] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="meeting-page">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#3A435E] hover:text-[#0A1128] transition-colors mb-10"
          data-testid="meeting-back"
        >
          <ArrowLeft size={14} strokeWidth={1.4} /> {t.meeting.back}
        </Link>

        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline mb-4">{t.meeting.overline}</div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-light leading-tight text-[#0A1128]">
              {t.meeting.title}
            </h1>
            <p className="mt-6 text-[#3A435E] leading-relaxed font-light">
              {t.meeting.subtitle}
            </p>
            <div className="mt-10 space-y-3 text-sm text-[#3A435E]">
              <p>stayzenpremium@gmail.com</p>
              <p>+351 935 316 194</p>
              <p>Lisboa, Portugal</p>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="md:col-span-7 bg-[#FCFAF8] border border-[#0A1128]/10 p-8 md:p-10 space-y-6"
            data-testid="meeting-form"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">{t.meeting.name}</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={fieldCls}
                  data-testid="meeting-name"
                />
              </div>
              <div>
                <label className="overline block mb-2">{t.meeting.email}</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={fieldCls}
                  data-testid="meeting-email"
                />
              </div>
            </div>

            <div>
              <label className="overline block mb-2">{t.meeting.phone}</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={fieldCls}
                data-testid="meeting-phone"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2 flex items-center gap-2">
                  <CalendarDays size={12} strokeWidth={1.4} /> {t.meeting.date}
                </label>
                <input
                  required
                  type="date"
                  min={today}
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className={fieldCls}
                  data-testid="meeting-date"
                />
              </div>
              <div>
                <label className="overline block mb-2 flex items-center gap-2">
                  <Clock size={12} strokeWidth={1.4} /> {t.meeting.time}
                </label>
                <input
                  required
                  type="time"
                  step={900}
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  className={fieldCls}
                  data-testid="meeting-time"
                />
              </div>
            </div>

            <div>
              <label className="overline block mb-2">{t.meeting.message}</label>
              <textarea
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${fieldCls} resize-none`}
                data-testid="meeting-message"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full md:w-auto bg-[#C5A059] text-[#0A1128] hover:bg-[#DCC287] transition-colors duration-300 px-8 py-4 text-xs tracking-[0.22em] uppercase disabled:opacity-60"
              data-testid="meeting-submit"
            >
              {loading ? "…" : t.meeting.submit}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MeetingPage;
